import { useState, useMemo, useEffect } from "react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { SelectItem as SelectItemType } from "@/components/ui/selectMulti";
import { Combobox } from "@/components/ui/combobox";
import { Checkbox } from "@/components/ui/checkbox";
import { useListMinimalUsers } from "@/lib/api/users";
import { useListCustomers } from "@/lib/api/customer";
import { CreateBeaconFundRequestValidation } from "@/lib/api/fund";
import { UseFormReturn } from "react-hook-form";

type FormData = z.infer<typeof CreateBeaconFundRequestValidation>;

interface BeaconFundRequestFormProps {
  form: UseFormReturn<FormData>;
  onSubmit: (data: FormData) => Promise<void>;
  isLoading?: boolean;
  isEditMode?: boolean;
  onCancel?: () => void;
  submitButtonText?: string;
  cancelButtonText?: string;
}

export function BeaconFundRequestForm({
  form,
  onSubmit,
  isLoading = false,
  isEditMode = false,
  onCancel,
  submitButtonText = isEditMode ? "Save Changes" : "Create Request",
  cancelButtonText = "Cancel"
}: BeaconFundRequestFormProps) {
  const [assignmentType, setAssignmentType] = useState<"customer" | "employee" | "customer_requested" | null>(null);
  const minimalCustomersListResult = useListCustomers({ minimal: true });
  const minimalUserListResult = useListMinimalUsers();

  const { minimalCustomerOptions, minimalEmployeeOptions } = useMemo(() => {
    const minimalCustomerOptions = (minimalCustomersListResult?.data?.data || []).map((customer: { id: string; long_name: string }) => {
      return ({ value: customer.id, label: customer.long_name });
    }).sort((a: SelectItemType, b: SelectItemType) => a.label.localeCompare(b.label));

    const minimalEmployeeOptions = (minimalUserListResult?.data?.data || []).map((user: { id: string; name: string }) => {
      return ({ value: user.id, label: user.name });
    }).sort((a: SelectItemType, b: SelectItemType) => a.label.localeCompare(b.label));

    return { minimalCustomerOptions, minimalEmployeeOptions };
  }, [minimalCustomersListResult?.data?.data, minimalUserListResult?.data?.data]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = form;

  const watchGranted = watch("has_been_granted");
  const watchCustomerId = watch("customer_id");
  const watchEmployeeId = watch("employee_id");
  const watchCustomerRequested = watch("customer_requested");


  // Set initial assignment type based on existing data in edit mode
  useEffect(() => {
    if (watchCustomerId) {
      setAssignmentType("customer");
    } else if (watchEmployeeId) {
      setAssignmentType("employee");
    } else if (watchCustomerRequested) {
      setAssignmentType("customer_requested");
    }
  }, [watchCustomerId, watchEmployeeId, watchCustomerRequested]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-2 gap-6">
      {/* Left Column - Survey Information */}
      <div className="space-y-6">
        <h3 className="text-lg font-semibold border-b pb-2">Survey Information</h3>
        
        {/* Recipient Information */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-gray-700">Recipient Information</h4>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="firstName">First Name *</Label>
              <Input
                id="firstName"
                {...register("survey.firstName")}
                placeholder="John"
              />
              {errors.survey?.firstName && (
                <p className="text-sm text-red-500 mt-1">{errors.survey.firstName.message}</p>
              )}
            </div>
            
            <div>
              <Label htmlFor="lastName">Last Name *</Label>
              <Input
                id="lastName"
                {...register("survey.lastName")}
                placeholder="Doe"
              />
              {errors.survey?.lastName && (
                <p className="text-sm text-red-500 mt-1">{errors.survey.lastName.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                {...register("survey.email")}
                placeholder="john.doe@example.com"
              />
              {errors.survey?.email && (
                <p className="text-sm text-red-500 mt-1">{errors.survey.email.message}</p>
              )}
            </div>
            
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                {...register("survey.phone")}
                placeholder="(555) 123-4567"
              />
            </div>
          </div>
        </div>

        {/* Organization Information */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-gray-700">Organization Information</h4>
          
          <div>
            <Label htmlFor="recipientOrganization">Recipient Organization *</Label>
            <Input
              id="recipientOrganization"
              {...register("survey.recipientOrganization")}
              placeholder="Organization name"
            />
            {errors.survey?.recipientOrganization && (
              <p className="text-sm text-red-500 mt-1">{errors.survey.recipientOrganization.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="recipientOrganizationMailingAddress">Mailing Address</Label>
            <Textarea
              id="recipientOrganizationMailingAddress"
              {...register("survey.recipientOrganizationMailingAddress")}
              placeholder="123 Main St, City, State ZIP"
              rows={2}
            />
          </div>

          <div>
            <Label htmlFor="organizationPurpose">Organization Purpose</Label>
            <Textarea
              id="organizationPurpose"
              {...register("survey.organizationPurpose")}
              placeholder="Describe the organization's purpose..."
              rows={3}
            />
          </div>
        </div>
      </div>

      {/* Right Column - Request Information */}
      <div className="space-y-6">
        <h3 className="text-lg font-semibold border-b pb-2">Request Information</h3>
        
        {/* Matching Information */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-gray-700">Survey Matching Information</h4>
          
          <div>
            <Label htmlFor="matchingGiftAmount">Survey Matching Gift Amount</Label>
            <Input
              id="matchingGiftAmount"
              {...register("survey.matchingGiftAmount")}
              placeholder="$1,000"
            />
          </div>
        </div>

        {/* Assignment */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-gray-700">Assignment {!isEditMode && "*"}</h4>
          
            <div>
              <Label>Assignment Type</Label>
              <Select
                value={assignmentType || ""}
                onValueChange={(value: "customer" | "employee" | "customer_requested") => {
                  setAssignmentType(value);
                  if (value === "customer") {
                    setValue("employee_id", null);
                    setValue("customer_requested", null);
                  } else if (value === 'employee'){
                    setValue("customer_id", null);
                    setValue("customer_requested", null);
                  } else if (value === "customer_requested") {
                    setValue("customer_id", null);
                    setValue("employee_id", null);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select assignment type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="customer">Customer</SelectItem>
                  <SelectItem value="employee">Employee</SelectItem>
                  <SelectItem value="customer_requested">Company Requested</SelectItem>
                </SelectContent>
              </Select>
            </div>

          {(assignmentType === "customer") && (
            <div className="grid gap-2">
              <Label>Customer</Label>
              <Combobox
                options={minimalCustomerOptions}
                value={watch("customer_id") || ""}
                onValueChange={(value: string) => {
                  setValue("customer_id", value);
                  if (isEditMode) setValue("employee_id", null);
                  if (isEditMode) setValue("customer_requested", null);
                }}
                placeholder="Select customer"
                isModal
                dwimOnChange
                popoverClassName="w-[400px]"
              />
            </div>
          )}

          {(assignmentType === "employee") && (
            <div className="grid gap-2">
              <Label>Employee</Label>
              <Combobox
                options={minimalEmployeeOptions}
                value={watch("employee_id") || ""}
                onValueChange={(value: string) => {
                  setValue("employee_id", value);
                  if (isEditMode) setValue("customer_id", null);
                  if (isEditMode) setValue("customer_requested", null);
                }}
                placeholder="Select employee"
                isModal
                dwimOnChange
                popoverClassName="w-[400px]"
              />
            </div>
          )}

          {(assignmentType === "customer_requested") && (
            <div className="grid gap-2">
              <Label>Company Requested</Label>
              <Input
                {...register("customer_requested")}
                onChange={(event) => {
                  if (event.preventDefault) { event.preventDefault(); }
                  setValue("customer_requested", event.target?.value);
                  if (isEditMode) setValue("customer_id", null);
                  if (isEditMode) setValue("employee_id", null);
                }}
              />
            </div>
          )}

          {errors.customer_id && (
            <p className="text-sm text-red-500">{errors.customer_id.message}</p>
          )}
        </div>

        {/* Grant Information */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-gray-700">Grant Information</h4>
          
          <div className="flex items-center space-x-2">
            {isEditMode ? (
              <>
                <Checkbox
                  id="has_been_granted"
                  checked={watchGranted}
                  onCheckedChange={(checked) => setValue("has_been_granted", !!checked)}
                />
                <Label htmlFor="has_been_granted" className="cursor-pointer">
                  Request Granted
                </Label>
              </>
            ) : (
              <>
                <input
                  type="checkbox"
                  id="has_been_granted"
                  {...register("has_been_granted")}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="has_been_granted" className="cursor-pointer">
                  Has been granted
                </Label>
              </>
            )}
          </div>

          {watchGranted && (
            <>
              <div>
                <Label htmlFor="amount_granted">Amount Granted *</Label>
                <Input
                  id="amount_granted"
                  type="number"
                  step="0.01"
                  {...register("amount_granted")}
                  placeholder="1000.00"
                />
                {errors.amount_granted && (
                  <p className="text-sm text-red-500 mt-1">{errors.amount_granted.message}</p>
                )}
              </div>

              <div className="flex items-center space-x-2">
                {isEditMode ? (
                  <>
                    <Checkbox
                      id="has_been_dispersed"
                      checked={watch("has_been_dispersed")}
                      onCheckedChange={(checked) => setValue("has_been_dispersed", !!checked)}
                    />
                    <Label htmlFor="has_been_dispersed" className="cursor-pointer">
                      Funds Disbursed
                    </Label>
                  </>
                ) : (
                  <>
                    <input
                      type="checkbox"
                      id="has_been_dispersed"
                      {...register("has_been_dispersed")}
                      className="rounded border-gray-300"
                    />
                    <Label htmlFor="has_been_dispersed" className="cursor-pointer">
                      Has been disbursed
                    </Label>
                  </>
                )}
              </div>
            </>
          )}

          {errors.has_been_dispersed && (
            <p className="text-sm text-red-500">{errors.has_been_dispersed.message}</p>
          )}
        </div>
      </div>

      <div className="col-span-2 flex justify-end space-x-2 pt-4 border-t">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
            {cancelButtonText}
          </Button>
        )}
        <Button type="submit" disabled={isLoading}>
          {isLoading ? (isEditMode ? "Saving..." : "Creating...") : submitButtonText}
        </Button>
      </div>
    </form>
  );
}
